import { inject, Injectable, signal } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { catchError, Observable, of, Subject, take, tap, timeout } from 'rxjs';
import { APP_CONSTANTS } from 'src/app/app.constants';
import {
  User,
  UserPermissionUpdate,
  UserWide,
} from 'src/app/core/interfaces/wellprotocol.interface';
import { AuthService } from './auth.service';
import { WsService } from './ws.service';

/**
 * A user as the app consumes it. Every protocol field is optional on the wire; the handlers serving
 * GetOneUser and SelectUserWide always populate the identity fields.
 */
export type KwmUser = Required<Pick<UserWide, 'id' | 'name' | 'firstName' | 'lastName'>> & {
  permission?: number;
  groups?: string[];
};

/** Maps a protocol record onto KwmUser. `permission` is passed through as-is, unassigned or
 *  not — resolving that is an authorization decision, owned by AuthService. */
function toKwmUser(record: User | UserWide): KwmUser {
  return {
    ...record,
    id: record.id ?? '',
    name: record.name ?? '',
    firstName: record.firstName ?? '',
    lastName: record.lastName ?? '',
  };
}

@Injectable({ providedIn: 'root' })
export class UserService {
  // WebSocket service for server communication
  private readonly ws = inject(WsService);
  // Toastr service for notifications
  private readonly toastr = inject(ToastrService);
  // Auth service for user access level
  private readonly auth = inject(AuthService);

  /**
   * List of all users. Public for component access.
   */
  public readonly users = signal<KwmUser[]>([]);

  /**
   * Currently logged-in user. Public for component access.
   */
  public readonly currentUser = signal<KwmUser | null>(null);

  /**
   * Subject to emit save result for user updates. Private, only used internally.
   */
  private readonly _saveResult$ = new Subject<boolean>();

  /**
   * Initializes WebSocket subscriptions for user-related events.
   */
  constructor() {
    // Listen for single user fetch response
    this.ws.subscribeWs(APP_CONSTANTS.RESPONSE_MESSAGES.GET_USER, (msg) => {
      if (!msg.body) return;
      const user = toKwmUser(msg.body['record'] as User);
      this.currentUser.set(user);
      this.auth.setPermission(user.permission);
    });

    // Listen for all users fetch response
    this.ws.subscribeWs(APP_CONSTANTS.RESPONSE_MESSAGES.GET_ALL_USERS, (msg) => {
      if (!msg.body) return;
      const records = (msg.body['records'] as UserWide[]) ?? [];
      // A record with no id cannot be tracked, keyed or saved.
      const usable = records.filter((record) => record.id);
      const discarded = records.length - usable.length;
      if (discarded > 0) {
        this.toastr.warning(
          `${discarded} user${discarded === 1 ? '' : 's'} could not be displayed due to incomplete data from the server.`,
        );
      }
      this.users.set(usable.map(toKwmUser));
    });

    // Listen for user update/save response
    this.ws.subscribeWs(APP_CONSTANTS.RESPONSE_MESSAGES.SET_USERS, (msg) => {
      const status = msg.header?.['status'];
      this._saveResult$.next(status === 4000);
    });

    // On connection established, fetch the current user by userId
    this.ws.subscribeWs(APP_CONSTANTS.SESSION_EVENTS.CONNECTION_ESTABLISHED, (msg) => {
      const userId = msg.body?.['userId'];
      if (userId) {
        this.ws.sendRequest({
          header: { name: APP_CONSTANTS.REQUEST_MESSAGES.GET_USER },
          body: { filter: { id: { $eq: userId } } },
        });
      }
    });
  }

  /**
   * Requests the list of all users from the server.
   */
  public getUsers(): void {
    this.ws.sendWsRequest({ name: APP_CONSTANTS.REQUEST_MESSAGES.GET_ALL_USERS }, {});
  }

  /**
   * Sends user access level updates to the server and returns an observable for the result.
   */
  public saveUsers(updates: UserPermissionUpdate[]): Observable<boolean> {
    this.ws.sendWsRequest({ name: APP_CONSTANTS.REQUEST_MESSAGES.SET_USERS }, { updates });
    return this._saveResult$.pipe(
      take(1),
      timeout(8000),
      tap((success) => {
        if (success) {
          this.toastr.success('User access levels saved successfully.');
        } else {
          this.toastr.error('Failed to save user access levels.', '', {
            disableTimeOut: true,
            tapToDismiss: true,
          });
        }
      }),
      catchError(() => {
        this.toastr.error('No response from server. Please try again.', '', {
          disableTimeOut: true,
          tapToDismiss: true,
        });
        return of(false);
      }),
    );
  }
}
